// WHAT: React component for the "Add New Aspect" modal in TimeTracQ settings
// WHY:  Provides a dedicated, reusable modal interface for users to create new aspects within workspaces
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: AspectConfig type
// RISKS:
//   - WARN: No internal validation of aspect name format or uniqueness.
//   - NOTE: Relies on parent for state management of modal visibility and new aspect data.

import type { AspectConfig } from "../types";

/**
 * WHAT:
 *   Interface for the data structure representing a new aspect to be added.
 *
 * WHY:
 *   Provides a clear and type-safe way to manage the form state for creating a new aspect.
 */
interface NewAspectData extends AspectConfig {
  aspectName: string;
  workspaceName: string;
}

/**
 * WHAT:
 *   Props interface for the `AddAspectModal` component.
 *
 * WHY:
 *   Defines the expected inputs for `AddAspectModal`, ensuring type safety and clarity
 *   regarding the modal's visibility, form data, available workspaces, and action callbacks.
 */
interface AddAspectModalProperties {
  aspectData: NewAspectData;
  isOpen: boolean;
  onAdd: () => Promise<void>;
  onAspectDataChange: (data: Partial<NewAspectData>) => void;
  onClose: () => void;
  saving: boolean;
  workspaceNames: string[];
}

/**
 * WHAT:
 *   React component that renders a modal for adding a new TimeTracQ aspect.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for the "Add Aspect" functionality,
 *   making the main component cleaner and improving reusability.
 *   It provides input fields for aspect name, hours, status, color, and boolean flags,
 *   along with a dropdown to select the parent workspace.
 *
 * PROPS:
 *   - isOpen: boolean — Controls the visibility of the modal.
 *   - onClose: () => void — Callback function to close the modal.
 *   - workspaceNames: string[] — An array of available workspace names for selection.
 *   - aspectData: NewAspectData — The current form data for the new aspect.
 *   - onAspectDataChange: (data: Partial<NewAspectData>) => void — Callback to update the form data.
 *   - onAdd: () => Promise<void> — Callback to trigger the aspect creation process.
 *   - saving: boolean — Indicates if a save operation is currently in progress, disabling the create button.
 *
 * BEHAVIOR:
 *   - Displays a modal with various input fields for aspect configuration.
 *   - The "Create Aspect" button is disabled if required fields are empty or a save operation is in progress.
 *   - Triggers `onAdd` when the "Create Aspect" button is clicked.
 *   - Triggers `onClose` when the "Cancel" button is clicked or the modal is closed externally.
 *
 * RISKS:
 *   - WARN: The modal itself does not perform comprehensive validation of aspect data (e.g., uniqueness, valid hours range);
 *           this is expected to be handled by the `onAdd` callback.
 *   - NOTE: The component is purely presentational and relies entirely on props for its state and behavior.
 *
 * @param root0
 *
 * @param root0.isOpen
 *
 * @param root0.onClose
 *
 * @param root0.workspaceNames
 *
 * @param root0.aspectData
 *
 * @param root0.onAspectDataChange
 *
 * @param root0.onAdd
 *
 * @param root0.saving
 *
 * @returns {JSX.Element} Modal component for adding a new aspect
 *
 */
export function AddAspectModal({
  aspectData,
  isOpen,
  onAdd,
  onAspectDataChange,
  onClose,
  saving,
  workspaceNames,
}: Readonly<AddAspectModalProperties>) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-white mb-4">Add New Aspect</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Workspace</label>
            <select
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => {
                onAspectDataChange({ workspaceName: e.target.value });
              }}
              value={aspectData.workspaceName}
            >
              <option value="">Select workspace...</option>
              {workspaceNames.map((workspaceName) => (
                <option key={workspaceName} value={workspaceName}>
                  {workspaceName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Aspect Name</label>
            <input
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => {
                onAspectDataChange({ aspectName: e.target.value });
              }}
              placeholder="Enter aspect name..."
              type="text"
              value={aspectData.aspectName}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Hours</label>
              <input
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                onChange={(e) => {
                  onAspectDataChange({ hours: Number.parseFloat(e.target.value) || 0 });
                }}
                step="0.5"
                type="number"
                value={aspectData.hours}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <select
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                  onAspectDataChange({ status: e.target.value });
                }}
                value={aspectData.status}
              >
                <option value="Include">Include</option>
                <option value="Exclude">Exclude</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Color</label>
            <div className="flex items-center space-x-2">
              <input
                className="w-12 h-10 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                onChange={(e) => {
                  onAspectDataChange({ color: e.target.value });
                }}
                type="color"
                value={aspectData.color}
              />
              <span className="text-sm text-slate-400">{aspectData.color}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Options</label>
            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center space-x-2">
                <input
                  checked={aspectData.carryover}
                  className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                  onChange={(e) => {
                    onAspectDataChange({ carryover: e.target.checked });
                  }}
                  type="checkbox"
                />
                <span className="text-sm text-slate-300">Carryover</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  checked={aspectData.count_hours}
                  className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                  onChange={(e) => {
                    onAspectDataChange({ count_hours: e.target.checked });
                  }}
                  type="checkbox"
                />
                <span className="text-sm text-slate-300">Count Hours</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  checked={aspectData.count_plan}
                  className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                  onChange={(e) => {
                    onAspectDataChange({ count_plan: e.target.checked });
                  }}
                  type="checkbox"
                />
                <span className="text-sm text-slate-300">Count Plan</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 text-white px-4 py-2 rounded-lg transition-colors"
            disabled={saving || !aspectData.workspaceName || !aspectData.aspectName.trim()}
            onClick={() => {
              void onAdd();
            }}
          >
            {saving ? "Creating..." : "Create Aspect"}
          </button>
        </div>
      </div>
    </div>
  );
}
